import {
  Box,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
  Grid,
  IconButton,
} from '@mui/material';
import { ArrowBackOutlined } from '@mui/icons-material/';
import { ArrowForwardOutlined } from '@mui/icons-material';
// import ArrowForwardOutlined from '@mui/icons-material/ArrowForwardOutlined';
import { FormCheckBoxField } from '../../../../presentation/components/form-check-box-field';
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  LossTypeCategory,
  LossType,
  transformApiDataToLossTypes,
} from '../mock-data/loss-types.mock';
import debounce from 'lodash.debounce';
import './loss-types-carousel.scss';

interface ApiResponse {
  data: {
    claims: {
      edges: Array<{
        node: {
          lossTypes: unknown[];
        };
      }>;
    };
  };
}

interface LossTypesCarouselProps {
  lossTypes?: unknown[] | ApiResponse;
  claimUuid?: string;
  isNotActive: boolean;
  onSelectionChange?: (selections: LossTypeSelections) => void;
  onAutoSave?: (updatedLossTypes: unknown[] | ApiResponse) => Promise<void>;
}

export interface LossTypeSelections {
  level1?: string;
  level2?: string;
  level3: string[];
  level4: string[];
  level5: string[];
}

export const LossTypesCarousel: React.FC<LossTypesCarouselProps> = ({
  lossTypes,
  claimUuid,
  isNotActive,
  onSelectionChange,
  onAutoSave,
}) => {
  const carouselRef = React.useRef<HTMLDivElement>(null);

  // State to track which columns are currently visible in the carousel view
  const [visibleColumnRange, setVisibleColumnRange] = useState<{
    start: number;
    end: number;
  }>({ start: 0, end: 3 });
  // State to track whether columns should be visible
  const [columnsVisible, setColumnsVisible] = useState<{
    level1: boolean;
    level2: boolean;
    level3: boolean;
    level4: boolean;
    level5: boolean;
  }>({
    level1: true,
    level2: false,
    level3: false,
    level4: false,
    level5: false,
  });

  // State for storing user selections at each level
  const [selections, setSelections] = useState<LossTypeSelections>({
    level1: undefined,
    level2: undefined,
    level3: [],
    level4: [],
    level5: [],
  });

  // Auto-save state
  const [hasFirstSelection, setHasFirstSelection] = useState(false);

  // Track if we're currently initializing from API data
  const [isInitializing, setIsInitializing] = useState(false);

  // Track if we've completed the first initialization to prevent clearing effects
  const [hasInitialized, setHasInitialized] = useState(false);

  // Transform API data to component format
  const lossTypesData = useMemo(() => {
    if (!lossTypes) {
      return [];
    }

    // Check if we received the full API response instead of just lossTypes
    let actualLossTypes = lossTypes;

    // If we got the full API response, extract the lossTypes
    if (lossTypes && typeof lossTypes === 'object' && 'data' in lossTypes) {
      const apiResponse = lossTypes as ApiResponse;
      if (apiResponse.data?.claims?.edges?.[0]?.node?.lossTypes) {
        actualLossTypes = apiResponse.data.claims.edges[0].node.lossTypes;
      }
    }

    try {
      const transformed = transformApiDataToLossTypes(actualLossTypes);
      return transformed;
    } catch (err) {
      console.error('Failed to transform API data:', err);
      return [];
    }
  }, [lossTypes]);

  // State to store the current hierarchy of available options based on selections
  const [availableOptions, setAvailableOptions] = useState<{
    level1: LossTypeCategory[];
    level2: LossType[];
    level3: LossType[];
    level4: LossType[];
    level5: LossType[];
  }>({
    level1: [],
    level2: [],
    level3: [],
    level4: [],
    level5: [],
  });

  // Update level1 options when data changes
  useEffect(() => {
    setAvailableOptions((prev) => ({
      ...prev,
      level1: lossTypesData,
    }));
  }, [lossTypesData]);

  // Restore initial selection states from API data
  useEffect(() => {
    // Extract the actual lossTypes array from the prop
    let actualLossTypes = lossTypes;

    // If we got the full API response, extract the lossTypes
    if (lossTypes && typeof lossTypes === 'object' && 'data' in lossTypes) {
      const apiResponse = lossTypes as ApiResponse;
      if (apiResponse.data?.claims?.edges?.[0]?.node?.lossTypes) {
        actualLossTypes = apiResponse.data.claims.edges[0].node.lossTypes;
      }
    }

    if (
      !actualLossTypes ||
      !Array.isArray(actualLossTypes) ||
      actualLossTypes.length === 0
    ) {
      return;
    }

    setIsInitializing(true);

    // Function to recursively extract selected items
    const extractSelections = (
      items: unknown[],
      level: number
    ): {
      selectedLevel1?: string;
      selectedLevel2?: string;
      selectedLevel3: string[];
      selectedLevel4: string[];
      selectedLevel5: string[];
    } => {
      const result = {
        selectedLevel1: undefined as string | undefined,
        selectedLevel2: undefined as string | undefined,
        selectedLevel3: [] as string[],
        selectedLevel4: [] as string[],
        selectedLevel5: [] as string[],
      };

      const processItems = (itemList: unknown[], currentLevel: number) => {
        itemList.forEach((item) => {
          if (
            item &&
            typeof item === 'object' &&
            'id' in item &&
            'selected' in item
          ) {
            const typedItem = item as {
              id: string;
              selected: boolean;
              options?: unknown[];
              children?: unknown[];
            };

            if (typedItem.selected) {
              if (currentLevel === 1) {
                result.selectedLevel1 = typedItem.id;
              } else if (currentLevel === 2) {
                result.selectedLevel2 = typedItem.id;
              } else if (currentLevel === 3) {
                result.selectedLevel3.push(typedItem.id);
              } else if (currentLevel === 4) {
                result.selectedLevel4.push(typedItem.id);
              } else if (currentLevel === 5) {
                result.selectedLevel5.push(typedItem.id);
              }
            }

            // Process children/options
            if (typedItem.options && Array.isArray(typedItem.options)) {
              processItems(typedItem.options, currentLevel + 1);
            } else if (
              typedItem.children &&
              Array.isArray(typedItem.children)
            ) {
              processItems(typedItem.children, currentLevel + 1);
            }
          }
        });
      };

      processItems(items, level);
      return result;
    };

    const initialSelections = extractSelections(actualLossTypes, 1);

    // Transform the data locally to avoid dependency issues
    const transformedData = transformApiDataToLossTypes(actualLossTypes);

    // Set initial selections
    setSelections({
      level1: initialSelections.selectedLevel1,
      level2: initialSelections.selectedLevel2,
      level3: initialSelections.selectedLevel3,
      level4: initialSelections.selectedLevel4,
      level5: initialSelections.selectedLevel5,
    });

    // Show columns based on what has selections
    setColumnsVisible({
      level1: true,
      level2:
        initialSelections.selectedLevel1 !== undefined ||
        initialSelections.selectedLevel2 !== undefined,
      level3:
        initialSelections.selectedLevel2 !== undefined ||
        initialSelections.selectedLevel3.length > 0,
      level4:
        initialSelections.selectedLevel3.length > 0 ||
        initialSelections.selectedLevel4.length > 0,
      level5:
        initialSelections.selectedLevel4.length > 0 ||
        initialSelections.selectedLevel5.length > 0,
    });

    // Populate available options based on initial selections
    if (initialSelections.selectedLevel1 && transformedData.length > 0) {
      const selectedCategory = transformedData.find(
        (category) => category.id === initialSelections.selectedLevel1
      );
      if (selectedCategory) {
        // Set level 2 options
        const level2Options = selectedCategory.options;

        // Build level 3 options from level 2 selection
        const level3Options: LossType[] = [];
        if (initialSelections.selectedLevel2) {
          const parentOption = level2Options.find(
            (option) => option.id === initialSelections.selectedLevel2
          );
          if (parentOption && parentOption.children) {
            level3Options.push(...parentOption.children);
          }
        }

        // Build level 4 options from level 3 selections
        const level4Options: LossType[] = [];
        initialSelections.selectedLevel3.forEach((selectedId) => {
          const parentOption = level3Options.find(
            (option) => option.id === selectedId
          );
          if (parentOption && parentOption.children) {
            level4Options.push(...parentOption.children);
          }
        });

        // Build level 5 options from level 4 selections
        const level5Options: LossType[] = [];
        initialSelections.selectedLevel4.forEach((selectedId) => {
          const parentOption = level4Options.find(
            (option) => option.id === selectedId
          );
          if (parentOption && parentOption.children) {
            level5Options.push(...parentOption.children);
          }
        });

        setAvailableOptions({
          level1: transformedData,
          level2: level2Options,
          level3: level3Options,
          level4: level4Options,
          level5: level5Options,
        });
      }
    }

    // End initialization phase after a delay to ensure all state updates complete
    setTimeout(() => {
      setIsInitializing(false);
      setHasInitialized(true);
    }, 0);
  }, [lossTypes]);

  // Helper function to update lossTypes data with current selections
  const updateLossTypesWithSelections = useCallback(
    (currentSelections: LossTypeSelections) => {
      // Extract the actual lossTypes array from the prop
      let actualLossTypes = lossTypes;
      let isFullApiResponse = false;

      // If we got the full API response, extract the lossTypes
      if (lossTypes && typeof lossTypes === 'object' && 'data' in lossTypes) {
        isFullApiResponse = true;
        const apiResponse = lossTypes as ApiResponse;
        if (apiResponse.data?.claims?.edges?.[0]?.node?.lossTypes) {
          actualLossTypes = apiResponse.data.claims.edges[0].node.lossTypes;
        }
      }

      if (!actualLossTypes || !Array.isArray(actualLossTypes)) {
        return lossTypes;
      }

      // Deep clone the lossTypes data
      const updatedLossTypes = JSON.parse(JSON.stringify(actualLossTypes));

      // Helper function to update selected state recursively
      const updateSelectedState = (
        items: unknown[],
        level: keyof LossTypeSelections,
        parentId?: string
      ) => {
        if (!items) return;

        items.forEach((item: unknown) => {
          if (
            item &&
            typeof item === 'object' &&
            'id' in item &&
            'selected' in item
          ) {
            if (level === 'level1') {
              // For level 1, update based on level1 selection
              (item as { selected: boolean }).selected =
                currentSelections.level1 === (item as { id: string }).id;
            } else if (level === 'level2') {
              // For level 2, check if this item's id matches the single selection
              (item as { selected: boolean }).selected =
                currentSelections.level2 === (item as { id: string }).id;
            } else {
              // For other levels, check if this item's id is in the selection array
              const levelSelections = currentSelections[level] as string[];
              (item as { selected: boolean }).selected =
                levelSelections.includes((item as { id: string }).id);
            }

            // Recursively update children
            if (
              'options' in item &&
              Array.isArray((item as { options: unknown[] }).options)
            ) {
              updateSelectedState(
                (item as { options: unknown[] }).options,
                level === 'level1'
                  ? 'level2'
                  : level === 'level2'
                  ? 'level3'
                  : level === 'level3'
                  ? 'level4'
                  : 'level5',
                (item as { id: string }).id
              );
            } else if (
              'children' in item &&
              Array.isArray((item as { children: unknown[] }).children)
            ) {
              updateSelectedState(
                (item as { children: unknown[] }).children,
                level === 'level1'
                  ? 'level2'
                  : level === 'level2'
                  ? 'level3'
                  : level === 'level3'
                  ? 'level4'
                  : 'level5',
                (item as { id: string }).id
              );
            }
          }
        });
      };

      // Start updating from level 1
      updateSelectedState(updatedLossTypes, 'level1');

      // If we got the full API response, reconstruct it with updated lossTypes
      if (isFullApiResponse) {
        const fullResponse = JSON.parse(JSON.stringify(lossTypes));
        (fullResponse as ApiResponse).data.claims.edges[0].node.lossTypes =
          updatedLossTypes;
        return fullResponse;
      }

      return updatedLossTypes;
    },
    [lossTypes]
  );

  // Auto-save function
  const autoSaveChanges = useCallback(
    async (currentSelections: LossTypeSelections) => {
      if (!onAutoSave || !claimUuid) {
        return;
      }

      try {
        const updatedLossTypes =
          updateLossTypesWithSelections(currentSelections);
        await onAutoSave(updatedLossTypes);
      } catch (error) {
        console.error('Loss types auto-save failed:', error);
      }
    },
    [claimUuid, onAutoSave, updateLossTypesWithSelections]
  );

  // Debounced auto-save with 2-second delay
  const triggerAutoSave = useMemo(
    () =>
      debounce((currentSelections: LossTypeSelections) => {
        autoSaveChanges(currentSelections);
      }, 2000),
    [autoSaveChanges]
  );

  // Helper method to remove duplicate items by ID with enhanced validation
  const removeDuplicatesById = useCallback((items: LossType[]): LossType[] => {
    if (!items || !Array.isArray(items)) {
      return [];
    }

    const seen = new Set<string>();
    return items.filter((item) => {
      if (!item || typeof item !== 'object' || !item.id) {
        return false;
      }

      if (seen.has(item.id)) {
        return false;
      }
      seen.add(item.id);
      return true;
    });
  }, []);

  // When level 1 selection changes, update level 2 options
  useEffect(() => {
    if (selections.level1) {
      const selectedCategory = lossTypesData.find(
        (category) => category.id === selections.level1
      );
      if (selectedCategory) {
        // Only update available options if we're not initializing (to avoid clearing pre-populated options)
        if (hasInitialized && !isInitializing) {
          setAvailableOptions((prev) => ({
            ...prev,
            level2: selectedCategory.options,
            level3: [],
            level4: [],
            level5: [],
          }));
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selections.level1, lossTypesData, isInitializing, hasInitialized]);

  // Update level 3 options based on level 2 selection
  useEffect(() => {
    const level3Options: LossType[] = [];

    // For the selected level 2 item, add its children to level 3 options
    if (selections.level2) {
      const parentOption = availableOptions.level2.find(
        (option) => option.id === selections.level2
      );
      if (parentOption && parentOption.children) {
        level3Options.push(...parentOption.children);
      }
    }

    // Remove duplicates by ID for level 3
    const deduplicatedLevel3Options = removeDuplicatesById(level3Options);

    setAvailableOptions((prev) => ({
      ...prev,
      level3: deduplicatedLevel3Options,
      level4: [],
      level5: [],
    }));

    // Only clear lower level selections when level 2 changes if we've already initialized
    if (hasInitialized && !isInitializing) {
      setSelections((prev) => ({
        ...prev,
      }));
    }
  }, [
    selections.level2,
    availableOptions.level2,
    isInitializing,
    hasInitialized,
    removeDuplicatesById,
  ]);

  // Update level 4 options based on level 3 selections
  useEffect(() => {
    if (!selections.level3 || selections.level3.length === 0) {
      setAvailableOptions((prev) => ({
        ...prev,
        level4: [],
        level5: [],
      }));
      return;
    }

    const level4Options: LossType[] = [];

    selections.level3.forEach((selectedId) => {
      const parentOption = availableOptions.level3.find(
        (option) => option.id === selectedId
      );
      if (
        parentOption &&
        parentOption.children &&
        Array.isArray(parentOption.children)
      ) {
        level4Options.push(...parentOption.children);
      }
    });

    // Remove duplicates by ID for level 4 with inline function to avoid dependency issues
    const seen = new Set<string>();
    const deduplicatedLevel4Options = level4Options.filter((item) => {
      if (!item || typeof item !== 'object' || !item.id || seen.has(item.id)) {
        return false;
      }
      seen.add(item.id);
      return true;
    });

    setAvailableOptions((prev) => ({
      ...prev,
      level4: deduplicatedLevel4Options,
      level5: [],
    }));

    // Only clear lower level selections when level 3 changes if we've already initialized
    if (hasInitialized && !isInitializing) {
      setSelections((prev) => ({
        ...prev,
      }));
    }
  }, [
    selections.level3,
    availableOptions.level3,
    isInitializing,
    hasInitialized,
  ]);

  // Update level 5 options based on level 4 selections
  useEffect(() => {
    if (!selections.level4 || selections.level4.length === 0) {
      setAvailableOptions((prev) => ({
        ...prev,
        level5: [],
      }));
      return;
    }

    const level5Options: LossType[] = [];

    selections.level4.forEach((selectedId) => {
      const parentOption = availableOptions.level4.find(
        (option) => option.id === selectedId
      );
      if (
        parentOption &&
        parentOption.children &&
        Array.isArray(parentOption.children)
      ) {
        level5Options.push(...parentOption.children);
      }
    });

    // Remove duplicates by ID for level 5 with inline function to avoid dependency issues
    const seen = new Set<string>();
    const deduplicatedLevel5Options = level5Options.filter((item) => {
      if (!item || typeof item !== 'object' || !item.id || seen.has(item.id)) {
        return false;
      }
      seen.add(item.id);
      return true;
    });

    setAvailableOptions((prev) => ({
      ...prev,
      level5: deduplicatedLevel5Options,
    }));

    // Only clear level 5 selections when level 4 changes if we've already initialized
    if (hasInitialized && !isInitializing) {
      setSelections((prev) => ({
        ...prev,
      }));
    }
  }, [
    selections.level4,
    availableOptions.level4,
    isInitializing,
    hasInitialized,
  ]);

  // Notify parent component when selections change
  useEffect(() => {
    if (onSelectionChange) {
      onSelectionChange(selections);
    }
  }, [selections, onSelectionChange]);

  // Handle radio button selection for level 1
  const handleLevel1Change = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;

    if (!hasFirstSelection) {
      setHasFirstSelection(true);
    }

    const newSelections = {
      ...selections,
      level1: value,
      // Clear lower level selections when level 1 changes
      level2: undefined,
      level3: [],
      level4: [],
      level5: [],
    };

    setSelections(newSelections);

    // Show level 2 column when level 1 is selected
    setColumnsVisible((prev) => ({
      ...prev,
      level2: true,
      // Hide higher levels until new selections are made
      level3: false,
      level4: false,
      level5: false,
    }));

    // Notify parent component
    if (onSelectionChange) {
      onSelectionChange(newSelections);
    }

    // Trigger auto-save
    if (hasFirstSelection || !hasFirstSelection) {
      triggerAutoSave(newSelections);
    }
  };

  // Handle radio button selection for level 2
  const handleLevel2Change = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;

    if (!hasFirstSelection) {
      setHasFirstSelection(true);
    }

    const newSelections = {
      ...selections,
      level2: value,
      // Clear lower level selections when level 2 changes
      level3: [],
      level4: [],
      level5: [],
    };

    setSelections(newSelections);

    // Show level 3 column when level 2 is selected
    setColumnsVisible((prev) => ({
      ...prev,
      level3: true,
      level4: false,
      level5: false,
    }));

    // Notify parent component
    if (onSelectionChange) {
      onSelectionChange(newSelections);
    }

    // Trigger auto-save
    if (hasFirstSelection || !hasFirstSelection) {
      triggerAutoSave(newSelections);
    }
  };

  // Handle checkbox selection for levels 3-5
  const handleCheckboxChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    level: 'level3' | 'level4' | 'level5'
  ) => {
    const { value, checked } = event.target;

    if (!hasFirstSelection) {
      setHasFirstSelection(true);
    }

    setSelections((prev) => {
      const currentSelections = [...prev[level]];
      let newSelections: LossTypeSelections;

      if (checked) {
        // Add to selections if checked
        // Show next level column when a checkbox is selected
        if (level === 'level3') {
          setColumnsVisible((prevColumns) => ({
            ...prevColumns,
            level4: true,
          }));
        } else if (level === 'level4') {
          setColumnsVisible((prevColumns) => ({
            ...prevColumns,
            level5: true,
          }));
        }

        newSelections = {
          ...prev,
          [level]: [...currentSelections, value],
        };
      } else {
        // Remove from selections if unchecked
        const updatedLevelSelections = currentSelections.filter(
          (id) => id !== value
        );

        // When unchecking an item, also clear all nested selections that were dependent on it
        let clearedNestedSelections = { ...prev };

        if (level === 'level3') {
          // Clear level 4 and 5 selections that were dependent on this level 3 item
          const dependentLevel4 = prev.level4.filter((level4Id) => {
            const parentOption = availableOptions.level3.find(
              (opt) => opt.id === value
            );
            return !parentOption?.children?.some(
              (child) => child.id === level4Id
            );
          });
          const dependentLevel5 = prev.level5.filter((level5Id) => {
            const parentOption = availableOptions.level3.find(
              (opt) => opt.id === value
            );
            return !parentOption?.children?.some((level4Opt) =>
              level4Opt.children?.some((child) => child.id === level5Id)
            );
          });

          clearedNestedSelections = {
            ...clearedNestedSelections,
            level4: dependentLevel4,
            level5: dependentLevel5,
          };
        } else if (level === 'level4') {
          // Clear level 5 selections that were dependent on this level 4 item
          const dependentLevel5 = prev.level5.filter((level5Id) => {
            const parentOption = availableOptions.level4.find(
              (opt) => opt.id === value
            );
            return !parentOption?.children?.some(
              (child) => child.id === level5Id
            );
          });

          clearedNestedSelections = {
            ...clearedNestedSelections,
            level5: dependentLevel5,
          };
        }

        // Hide next levels if all checkboxes in the current level are unchecked
        if (level === 'level3' && updatedLevelSelections.length === 0) {
          setColumnsVisible((prevColumns) => ({
            ...prevColumns,
            level4: false,
            level5: false,
          }));
        } else if (level === 'level4' && updatedLevelSelections.length === 0) {
          setColumnsVisible((prevColumns) => ({
            ...prevColumns,
            level5: false,
          }));
        }

        newSelections = {
          ...clearedNestedSelections,
          [level]: updatedLevelSelections,
        };
      }

      // Notify parent component
      if (onSelectionChange) {
        onSelectionChange(newSelections);
      }

      // Trigger auto-save
      if (hasFirstSelection || !hasFirstSelection) {
        triggerAutoSave(newSelections);
      }

      return newSelections;
    });
  };

  // Calculate which levels are active based on selections and visibility
  const activeLevels = useMemo(() => {
    const levels = ['level1', 'level2', 'level3', 'level4', 'level5'];
    return levels.filter(
      (_, index) =>
        columnsVisible[`level${index + 1}` as keyof typeof columnsVisible]
    );
  }, [columnsVisible]);

  // Handle carousel navigation
  const handleScrollLeft = () => {
    if (visibleColumnRange.start > 0) {
      const newStart = Math.max(0, visibleColumnRange.start - 1);
      const newEnd = newStart + 3;
      setVisibleColumnRange({ start: newStart, end: newEnd });

      if (carouselRef.current) {
        const columnWidth = carouselRef.current.clientWidth / 4;
        carouselRef.current.scrollTo({
          left: newStart * columnWidth,
          behavior: 'smooth',
        });
      }
    }
  };

  const handleScrollRight = () => {
    const maxIndex = activeLevels.length - 1;
    if (visibleColumnRange.end < maxIndex) {
      const newEnd = Math.min(maxIndex, visibleColumnRange.end + 1);
      const newStart = newEnd - 3;
      setVisibleColumnRange({ start: newStart, end: newEnd });

      if (carouselRef.current) {
        const columnWidth = carouselRef.current.clientWidth / 4;
        carouselRef.current.scrollTo({
          left: newStart * columnWidth,
          behavior: 'smooth',
        });
      }
    }
  };

  // Helper function to check if a level should be shown in current carousel view
  const isColumnVisible = (levelIndex: number) => {
    const adjustedIndex = levelIndex - 1; // Convert from 1-based to 0-based index
    return (
      adjustedIndex >= visibleColumnRange.start &&
      adjustedIndex <= visibleColumnRange.end
    );
  };

  return (
    <Box className="loss-types-carousel">
      {/* Complete new structure with arrows outside main container */}
      <Box className="carousel-navigation-wrapper">
        {/* Left navigation arrow - now outside main container */}
        <Box className="carousel-arrow-container left-arrow-container">
          <IconButton
            className="carousel-arrow carousel-arrow-left"
            onClick={handleScrollLeft}
            aria-label="Scroll left"
            disabled={visibleColumnRange.start === 0}
            size="large"
          >
            <ArrowBackOutlined fontSize="medium" />
          </IconButton>
        </Box>

        {/* Main carousel container */}
        <Box className="carousel-container">
          <Box className="loss-types-content" ref={carouselRef}>
            <Grid container spacing={2} className="loss-types-grid">
              {/* Level 1: Radio buttons for First Party vs Third Party */}
              {columnsVisible.level1 && isColumnVisible(1) && (
                <Grid
                  item
                  xs={12}
                  md={3} // Each column takes up 1/4 of the carousel
                  className="loss-types-column"
                >
                  <Box className="loss-types-level">
                    <Typography variant="h6" className="loss-types-level-title">
                      LEVEL 1
                    </Typography>
                    <FormControl component="fieldset">
                      <RadioGroup
                        value={selections.level1 || ''}
                        onChange={handleLevel1Change}
                      >
                        {availableOptions.level1.map((category) => (
                          <FormControlLabel
                            key={category.id}
                            value={category.id}
                            control={<Radio />}
                            label={category.label}
                            className="loss-type-option"
                          />
                        ))}
                      </RadioGroup>
                    </FormControl>
                  </Box>
                </Grid>
              )}

              {/* Level 2: Radio buttons for loss types based on level 1 selection */}
              {columnsVisible.level2 && isColumnVisible(2) && (
                <Grid
                  item
                  xs={12}
                  md={3} // Each column takes up 1/4 of the carousel
                  className="loss-types-column"
                >
                  {availableOptions.level2.length > 0 && (
                    <Box className="loss-types-level">
                      <Typography
                        variant="h6"
                        className="loss-types-level-title"
                      >
                        LEVEL 2
                      </Typography>
                      <FormControl component="fieldset">
                        <RadioGroup
                          value={selections.level2 || ''}
                          onChange={handleLevel2Change}
                        >
                          {availableOptions.level2.map((option) => (
                            <FormControlLabel
                              key={option.id}
                              value={option.id}
                              control={<Radio />}
                              label={option.label}
                              className="loss-type-option"
                            />
                          ))}
                        </RadioGroup>
                      </FormControl>
                    </Box>
                  )}
                </Grid>
              )}

              {/* Level 3: Checkboxes for loss types based on level 2 selections */}
              {columnsVisible.level3 && isColumnVisible(3) && (
                <Grid
                  item
                  xs={12}
                  md={3} // Each column takes up 1/4 of the carousel
                  className="loss-types-column"
                >
                  {availableOptions.level3.length > 0 && (
                    <Box className="loss-types-level">
                      <Typography
                        variant="h6"
                        className="loss-types-level-title"
                      >
                        LEVEL 3
                      </Typography>
                      <Box className="loss-types-checkboxes">
                        {availableOptions.level3.length > 0 &&
                          availableOptions.level3.map((option) => (
                            <Box
                              key={option.id}
                              className="loss-type-option-container"
                            >
                              <FormCheckBoxField
                                name={`level3-${option.id}`}
                                label={option.label}
                                checked={selections.level3.includes(option.id)}
                                handleChange={(event) => {
                                  const syntheticEvent = {
                                    target: {
                                      value: option.id,
                                      checked: event.target.checked,
                                    },
                                  } as React.ChangeEvent<HTMLInputElement>;
                                  handleCheckboxChange(
                                    syntheticEvent,
                                    'level3'
                                  );
                                }}
                                labelPlacement="end"
                                customFont="14px"
                                customWeight="500"
                              />
                            </Box>
                          ))}
                        {/* {availableOptions.level3.length === 0 && (
                    <Typography className="no-options-message">
                      No options available. Please select level 2 options.
                    </Typography>
                  )} */}
                      </Box>
                    </Box>
                  )}
                </Grid>
              )}

              {/* Level 4: Checkboxes for loss types based on level 3 selections */}
              {columnsVisible.level4 &&
                isColumnVisible(4) &&
                availableOptions.level4.length > 0 && (
                  <Grid
                    item
                    xs={12}
                    md={3} // Each column takes up 1/4 of the carousel
                    className="loss-types-column"
                  >
                    <Box className="loss-types-level">
                      <Typography
                        variant="h6"
                        className="loss-types-level-title"
                      >
                        LEVEL 4
                      </Typography>
                      <Box className="loss-types-checkboxes">
                        {availableOptions.level4.map((option) => (
                          <Box
                            key={option.id}
                            className="loss-type-option-container"
                          >
                            <FormCheckBoxField
                              name={`level4-${option.id}`}
                              label={option.label}
                              checked={selections.level4.includes(option.id)}
                              handleChange={(event) => {
                                const syntheticEvent = {
                                  target: {
                                    value: option.id,
                                    checked: event.target.checked,
                                  },
                                } as React.ChangeEvent<HTMLInputElement>;
                                handleCheckboxChange(syntheticEvent, 'level4');
                              }}
                              labelPlacement="end"
                              customFont="14px"
                              customWeight="500"
                            />
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  </Grid>
                )}

              {/* Level 5: Checkboxes for loss types based on level 4 selections */}
              {columnsVisible.level5 &&
                isColumnVisible(5) &&
                availableOptions.level5.length > 0 && (
                  <Grid item xs={12} md={4} className="loss-types-column">
                    <Box className="loss-types-level">
                      <Typography
                        variant="h6"
                        className="loss-types-level-title"
                      >
                        LEVEL 5
                      </Typography>
                      <Box className="loss-types-checkboxes">
                        {availableOptions.level5.map((option) => (
                          <Box
                            key={option.id}
                            className="loss-type-option-container"
                          >
                            <FormCheckBoxField
                              name={`level5-${option.id}`}
                              label={option.label}
                              checked={selections.level5.includes(option.id)}
                              handleChange={(event) => {
                                const syntheticEvent = {
                                  target: {
                                    value: option.id,
                                    checked: event.target.checked,
                                  },
                                } as React.ChangeEvent<HTMLInputElement>;
                                handleCheckboxChange(syntheticEvent, 'level5');
                              }}
                              labelPlacement="end"
                              customFont="14px"
                              customWeight="500"
                            />
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  </Grid>
                )}
            </Grid>
          </Box>
        </Box>

        {/* Right navigation arrow - now outside main container */}
        <Box className="carousel-arrow-container right-arrow-container">
          <IconButton
            className="carousel-arrow carousel-arrow-right"
            onClick={handleScrollRight}
            aria-label="Scroll right"
            disabled={visibleColumnRange.end >= activeLevels.length - 1}
            size="large"
          >
            <ArrowForwardOutlined fontSize="medium" />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
};
